import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Mutable, PropertyValuePair, SelectedMutations } from '@feedyou/utils'
import { AnswerSchema, AnswerStyle, DialogId, QuestionStepSchema, StepId } from '@feedyou/schema'

import { generateId } from '../../../lib/generateId'
import { updateMutableValue } from '../../Step/StepService'

type QuestionStepReducerState = Mutable<QuestionStepSchema>
type WithMutations<T extends object> = T & { selectedMutations: SelectedMutations | null }

const slice = createSlice({
  name: 'steps/question',
  initialState: {} as QuestionStepReducerState,
  reducers: {
    addAnswer: (step, action: PayloadAction<void>) => {
      if (!step.answers) {
        step.answers = []
      }
      step.answers.push({ id: generateId.stepId(), answer: '' })
    },
    addDefaultAnswer: (step, action: PayloadAction<void>) => {
      step.defaultAnswer = { id: `${generateId.stepId()}-default` }
    },
    editProperty: (
      step,
      action: PayloadAction<
        WithMutations<
          PropertyValuePair<QuestionStepSchema> & {
            answerId: StepId
            selectedMutations: SelectedMutations
          }
        >
      >
    ) => {
      const { answerId, property, value, selectedMutations } = action.payload
      step.answers = step.answers?.map((answerItem: any) => {
        if (answerItem.id !== answerId) return answerItem
        if (!selectedMutations) return { ...answerItem, answer: value }
        return updateMutableValue(answerItem, property, value, selectedMutations)
      })
    },
    editDefaultAnswerProperty: (
      step,
      action: PayloadAction<WithMutations<PropertyValuePair<QuestionStepSchema>>>
    ) => {
      step.defaultAnswer = updateMutableValue(
        (step.defaultAnswer as any) || {},
        action.payload.property,
        action.payload.value,
        action.payload.selectedMutations
      )
    },
    setQuestion: (
      step,
      action: PayloadAction<{ question: string; selectedMutations: SelectedMutations }>
    ) => {
      const { question, selectedMutations } = action.payload
      return updateMutableValue(step, 'question', question, selectedMutations)
    },
    setQuestionFirst: (
      step,
      action: PayloadAction<{ questionFirst?: string; selectedMutations: SelectedMutations }>
    ) => {
      const { questionFirst, selectedMutations } = action.payload
      return updateMutableValue(step, 'questionFirst', questionFirst || '', selectedMutations)
    },
    setAnswer: (
      step,
      action: PayloadAction<
        WithMutations<{
          answerText: string
          answerIndex: number
          selectedMutations: SelectedMutations
        }>
      >
    ) => {
      const { answerText, answerIndex, selectedMutations } = action.payload
      step.answers = step.answers?.map((answer: AnswerSchema, index: number) => {
        if (index !== answerIndex) return answer
        return updateMutableValue(answer as any, 'answer', answerText, selectedMutations) // Tady se ta step mutation funkce zdánlivě zneužívá k button itemu
      })
    },
    setStorage: (step, action: PayloadAction<string | undefined>) => {
      step.storage = action.payload
    },
    setListStyle: (step, action: PayloadAction<AnswerStyle | undefined>) => {
      step.listStyle = action.payload
    },
    setAnswerDialog: (step, action: PayloadAction<{ dialogId: DialogId; answerIndex: number }>) => {
      const { answerIndex, dialogId } = action.payload
      if (!step.answers?.[answerIndex]) return
      step.answers[answerIndex].dialog = dialogId
    },
    setSkipAnswer: (
      step,
      action: PayloadAction<{ answerText: string; selectedMutations: SelectedMutations }>
    ) => {
      const { answerText, selectedMutations } = action.payload
      return updateMutableValue(step, 'skipAnswer', answerText, selectedMutations)
    },
    setSkipFirstAnswer: (
      step,
      action: PayloadAction<{ answerText: string; selectedMutations: SelectedMutations }>
    ) => {
      const { answerText, selectedMutations } = action.payload
      return updateMutableValue(step, 'skipFirstAnswer', answerText, selectedMutations)
    },
    setAllAnswer: (
      step,
      action: PayloadAction<{ answerText: string; selectedMutations: SelectedMutations }>
    ) => {
      const { answerText, selectedMutations } = action.payload
      return updateMutableValue(step, 'allAnswer', answerText, selectedMutations)
    },
    setOtherAnswer: (
      step,
      action: PayloadAction<{ answerText: string; selectedMutations: SelectedMutations }>
    ) => {
      const { answerText, selectedMutations } = action.payload
      return updateMutableValue(step, 'otherAnswer', answerText, selectedMutations)
    },
    deleteAnswer: (
      step,
      action: PayloadAction<{ answerIndex: number; selectedMutations: SelectedMutations }>
    ) => {
      step.answers = step.answers?.filter(
        (_: AnswerSchema, index: number) => index !== action.payload.answerIndex
      )
    },
    deleteDefaultAnswer: (step, action: PayloadAction<void>) => {
      delete step.defaultAnswer
    }
  }
})

export const actions = slice.actions
export const reducer = slice.reducer
